import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const RESEND_COOLDOWN_SECONDS = 60;

function Login() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login, requestLoginOtp, verifyLoginOtp } = useAuth();

    const [loginWithOtp, setLoginWithOtp] = useState(false);
    const [formData, setFormData] = useState({
        identifier: '',
        password: '',
        otp: '',
    });
    const [otpStep, setOtpStep] = useState('identifier');
    const [cooldown, setCooldown] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [userNotFound, setUserNotFound] = useState(false);
    const [infoMessage, setInfoMessage] = useState('');

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((previous) => ({ ...previous, [name]: value }));
    };

    const handleToggleLoginMode = (event) => {
        if (!event.target.checked) {
            return;
        }

        const switchToOtp = !loginWithOtp;
        setLoginWithOtp(switchToOtp);
        setOtpStep('identifier');
        setErrorMessage('');
        setInfoMessage('');
        setUserNotFound(false);
        setFormData((previous) => ({
            ...previous,
            otp: '',
            password: switchToOtp ? '' : previous.password,
        }));
    };

    const handlePasswordSubmit = async (event) => {
        event.preventDefault();
        setIsSubmitting(true);
        setErrorMessage('');
        setUserNotFound(false);

        const result = await login({
            identifier: formData.identifier.trim(),
            password: formData.password,
        });

        setIsSubmitting(false);

        if (result.success) {
            navigate('/my-account', { replace: true });
            return;
        }

        if (result.code === 'USER_NOT_FOUND') {
            setUserNotFound(true);
            return;
        }

        if (result.code === 'EMAIL_NOT_VERIFIED') {
            navigate('/verify-otp', {
                replace: true,
                state: {
                    email: formData.identifier.trim(),
                    message: result.message || 'Please verify your email with OTP before login.',
                    autoSendOtp: true,
                },
            });
            return;
        }

        setErrorMessage(result.message || 'Unable to login.');
    };

    const handleOtpRequest = async (event) => {
        event.preventDefault();
        setErrorMessage('');
        setInfoMessage('');
        const identifier = formData.identifier.trim();
        if (!identifier) {
            setErrorMessage('Email or mobile number is required.');
            return;
        }
        setIsSubmitting(true);
        const result = await requestLoginOtp({ identifier });
        setIsSubmitting(false);
        if (!result.success) {
            setErrorMessage(result.message || 'Unable to send OTP.');
            return;
        }
        setOtpStep('otp');
        setInfoMessage(`OTP sent to ${result.data?.maskedDestination || 'your contact'}.`);
        setCooldown(RESEND_COOLDOWN_SECONDS);
    };

    const handleOtpVerify = async (event) => {
        event.preventDefault();
        setErrorMessage('');
        setIsSubmitting(true);
        const result = await verifyLoginOtp({
            identifier: formData.identifier.trim(),
            otp: formData.otp.trim(),
        });
        setIsSubmitting(false);
        if (!result.success) {
            setErrorMessage(result.message || 'Unable to verify OTP.');
            return;
        }
        const profileIncomplete = result.data?.profileIncomplete;
        navigate('/my-account', {
            replace: true,
            state: profileIncomplete
                ? { completeProfile: true, message: 'Please complete your profile details.' }
                : undefined,
        });
    };

    const handleResendOtp = async () => {
        if (cooldown > 0) {
            return;
        }
        const result = await requestLoginOtp({ identifier: formData.identifier.trim() });
        if (!result.success) {
            setErrorMessage(result.message || 'Unable to resend OTP.');
            return;
        }
        setInfoMessage('OTP sent again.');
        setCooldown(RESEND_COOLDOWN_SECONDS);
    };

    useEffect(() => {
        if (cooldown <= 0) {
            return undefined;
        }
        const timerId = window.setTimeout(() => setCooldown((value) => Math.max(0, value - 1)), 1000);
        return () => window.clearTimeout(timerId);
    }, [cooldown]);

    const identifierPlaceholder = loginWithOtp
        ? 'Enter your email or 10-digit mobile number'
        : 'Username, email, or mobile';

    const showOtpEntry = loginWithOtp && otpStep === 'otp';

    return (
    <div className="page-content pt-20 pb-20">
            <div className="container">
                <div className="row">
                    <div className="col-xl-8 col-lg-10 col-md-12 m-auto">
                        <div className="row">
                            <div className="col-lg-6 pr-30 d-none d-lg-block">
                                <img className="border-radius-15" src="assets/imgs/page/login-1.png" alt=""/>
                            </div>
                            <div className="col-lg-6 col-md-8">
                                <div className="login_wrap widget-taber-content background-white">
                                    <div className="padding_eight_all bg-white">
                                        <div className="heading_s1">
                                            <h1 className="mb-5">Login</h1>
                                            <p className="mb-20">Don&apos;t have an account? <Link to="/register">Create here</Link></p>
                                        </div>
                                        {location.state?.message ? <div className="alert alert-info py-2">{location.state.message}</div> : null}
                                        {infoMessage ? <div className="alert alert-info py-2">{infoMessage}</div> : null}
                                        {userNotFound ? (
                                            <div className="alert alert-warning py-2">
                                                This user does not exist. You can{' '}
                                                <Link to="/register" state={{ email: formData.identifier }}>
                                                    click here to create your account
                                                </Link>
                                                .
                                            </div>
                                        ) : null}
                                        {errorMessage ? <div className="alert alert-danger py-2">{errorMessage}</div> : null}

                                        {showOtpEntry ? (
                                            <form onSubmit={handleOtpVerify}>
                                                <div className="form-group">
                                                    <label className="text-muted text-uppercase small mb-5 d-block">Email or mobile</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={formData.identifier}
                                                        readOnly
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <label className="text-muted text-uppercase small mb-5 d-block">OTP</label>
                                                    <input
                                                        type="text"
                                                        required
                                                        name="otp"
                                                        value={formData.otp}
                                                        onChange={handleChange}
                                                        placeholder="6-digit OTP"
                                                        maxLength={6}
                                                    />
                                                </div>
                                                <div className="form-group d-flex justify-content-between align-items-center">
                                                    <button type="button" className="btn btn-link p-0" onClick={() => setOtpStep('identifier')}>
                                                        Change email/mobile
                                                    </button>
                                                    <button type="button" className="btn btn-link p-0" onClick={handleResendOtp} disabled={cooldown > 0}>
                                                        {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend OTP'}
                                                    </button>
                                                </div>
                                                <div className="custome-checkbox mb-20">
                                                    <input
                                                        key="login-mode-otp-verify"
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id="loginModeOtpVerify"
                                                        onChange={handleToggleLoginMode}
                                                    />
                                                    <label className="form-check-label" htmlFor="loginModeOtpVerify">
                                                        <span>Login with password</span>
                                                    </label>
                                                </div>
                                                <div className="form-group">
                                                    <button type="submit" className="btn btn-heading btn-block hover-up" disabled={isSubmitting}>
                                                        {isSubmitting ? 'Verifying...' : 'Verify & Login'}
                                                    </button>
                                                </div>
                                            </form>
                                        ) : (
                                            <form onSubmit={loginWithOtp ? handleOtpRequest : handlePasswordSubmit}>
                                                <div className="form-group">
                                                    <label className="text-muted text-uppercase small mb-5 d-block">
                                                        {loginWithOtp ? 'Email or mobile number' : 'Username, email, or mobile'}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        required
                                                        name="identifier"
                                                        value={formData.identifier}
                                                        onChange={handleChange}
                                                        placeholder={identifierPlaceholder}
                                                    />
                                                </div>
                                                {!loginWithOtp ? (
                                                    <div className="form-group">
                                                        <input
                                                            required
                                                            type="password"
                                                            name="password"
                                                            value={formData.password}
                                                            onChange={handleChange}
                                                            placeholder="Your password *"
                                                        />
                                                    </div>
                                                ) : null}
                                                {!loginWithOtp ? (
                                                    <div className="login_footer form-group">
                                                        <Link className="text-muted" to="/password-reset">Forgot password?</Link>
                                                    </div>
                                                ) : null}
                                                <div className="custome-checkbox form-group mb-20">
                                                    <input
                                                        key={loginWithOtp ? 'login-mode-otp' : 'login-mode-password'}
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id="loginModeToggle"
                                                        onChange={handleToggleLoginMode}
                                                    />
                                                    <label className="form-check-label" htmlFor="loginModeToggle">
                                                        <span>{loginWithOtp ? 'Login with password' : 'Login with OTP'}</span>
                                                    </label>
                                                </div>
                                                <div className="form-group">
                                                    <button type="submit" className="btn btn-heading btn-block hover-up" disabled={isSubmitting}>
                                                        {loginWithOtp
                                                            ? (isSubmitting ? 'Sending...' : 'Continue')
                                                            : (isSubmitting ? 'Logging in...' : 'Log in')}
                                                    </button>
                                                </div>
                                            </form>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
  );
}
export default Login;
